// Interaktiv fortælling — prikker for fast tekst, understregning for frie ord.

import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type Dispatch,
  type ReactNode,
  type SetStateAction,
} from 'react';
import type {
  KeyboardLayoutId,
  PracticeMode,
  RoundResult,
  Trin,
  UserProfile,
} from '../../types';
import { getStoryScript } from '../../data/stories';
import { playStoryMagicCue } from '../../lib/audio/storyMagicCue';
import { playSuccessChime } from '../../lib/audio/successChime';
import type { PreparedRound } from '../../lib/lessonGenerator';
import {
  beatSlotsFilled,
  beatSpeakText,
  getBlankHint,
  isListenOnlyBeat,
  segmentLiteralSpeech,
  lastLiteralSpeechBefore,
  parseBeatSegments,
  resolveStoryTemplate,
  syncMorphologyForSlots,
  type StorySegment,
  type StorySlotMorph,
} from '../../lib/story';
import { useDictationAudio } from '../../lib/useDictationAudio';
import { useEnterAction } from '../../lib/useEnterAction';
import { useRoundInput } from '../../lib/useRoundInput';
import { isPrintableTypingKey, shouldIgnoreKeyEvent } from '../../lib/typingKey';
import Keyboard from '../Keyboard';
import SpaceAdvanceHint from '../SpaceAdvanceHint';

interface StoryRoundProps {
  round: PreparedRound;
  trin: Trin;
  keyboardLayout: KeyboardLayoutId;
  user: UserProfile;
  strictCase?: boolean;
  allowAltLayer?: boolean;
  onComplete: (
    result: RoundResult,
    action: 'dashboard' | 'another',
  ) => void;
}

type SegmentPhase =
  | 'speaking'
  | 'typingLiteral'
  | 'typingBlank'
  | 'listenOnly';

interface StatsAccum {
  totalKeystrokes: number;
  correctChars: number;
  mistakes: number;
  backspaces: number;
  errorsByKey: Record<string, number>;
  responseTimes: Record<string, number[]>;
  durationMs: number;
  hintsUsed: boolean;
}

function emptyStats(): StatsAccum {
  return {
    totalKeystrokes: 0,
    correctChars: 0,
    mistakes: 0,
    backspaces: 0,
    errorsByKey: {},
    responseTimes: {},
    durationMs: 0,
    hintsUsed: false,
  };
}

function mergeStats(into: StatsAccum, r: RoundResult): void {
  into.totalKeystrokes += r.totalKeystrokes;
  into.correctChars += r.correctChars;
  into.mistakes += r.mistakes;
  into.backspaces += r.backspaces;
  into.durationMs += r.durationMs;
  if (r.hintsUsed) into.hintsUsed = true;
  for (const [k, v] of Object.entries(r.errorsByKey)) {
    into.errorsByKey[k] = (into.errorsByKey[k] ?? 0) + v;
  }
  for (const [k, samples] of Object.entries(r.responseTimes)) {
    into.responseTimes[k] = [...(into.responseTimes[k] ?? []), ...samples];
  }
}

function statsToResult(
  accum: StatsAccum,
  meta: {
    roundId: string;
    roundTitle: string;
    mode: PracticeMode;
    targetLength: number;
    completed: boolean;
  },
): RoundResult {
  const accuracy =
    accum.totalKeystrokes === 0
      ? 1
      : accum.correctChars / accum.totalKeystrokes;
  const wpm =
    accum.durationMs <= 0
      ? 0
      : accum.correctChars / 5 / (accum.durationMs / 60_000);

  return {
    roundId: meta.roundId,
    title: meta.roundTitle,
    mode: meta.mode,
    targetLength: meta.targetLength,
    durationMs: accum.durationMs,
    wpm,
    accuracy,
    correctChars: accum.correctChars,
    totalKeystrokes: accum.totalKeystrokes,
    mistakes: accum.mistakes,
    backspaces: accum.backspaces,
    errorsByKey: accum.errorsByKey,
    responseTimes: accum.responseTimes,
    completed: meta.completed,
    hintsUsed: accum.hintsUsed,
    presentationKind: 'story',
  };
}

function blankDisplayWidth(maxLength: number): number {
  return Math.min(12, Math.max(6, maxLength));
}

export default function StoryRound({
  round,
  trin,
  keyboardLayout,
  user,
  strictCase,
  allowAltLayer,
  onComplete,
}: StoryRoundProps) {
  const presentation = round.template.presentation;
  const storyId =
    presentation?.kind === 'story' ? presentation.storyId : null;
  const script = storyId ? getStoryScript(storyId) : undefined;
  const mode = round.template.mode;
  const {
    voicesReady,
    audioAvailable,
    showNoVoiceBanner,
    audioEnabled,
    speak,
    cancel,
  } = useDictationAudio(user);

  const [hasStarted, setHasStarted] = useState(false);
  const [showReveal, setShowReveal] = useState(false);
  const [showHint, setShowHint] = useState(false);
  const [beatIndex, setBeatIndex] = useState(0);
  const [segmentIndex, setSegmentIndex] = useState(0);
  const [phase, setPhase] = useState<SegmentPhase>('speaking');
  const [slotValues, setSlotValues] = useState<Record<string, string>>({});
  const [slotMorph, setSlotMorph] = useState<Record<string, StorySlotMorph>>(
    {},
  );
  const [completedLines, setCompletedLines] = useState<string[]>([]);
  const [storyComplete, setStoryComplete] = useState(false);
  const [blankBuffer, setBlankBuffer] = useState('');
  const [narrating, setNarrating] = useState(false);
  const statsRef = useRef(emptyStats());
  const targetLenRef = useRef(0);
  const speakRunningRef = useRef(false);
  const listenOnlySpokenRef = useRef(false);
  const slotValuesRef = useRef(slotValues);
  slotValuesRef.current = slotValues;

  const [slotsFrozenAtBeat, setSlotsFrozenAtBeat] = useState<
    Record<string, string>
  >({});

  const currentBeat = script?.beats[beatIndex];
  const isListenOnly =
    currentBeat !== undefined && isListenOnlyBeat(currentBeat);

  useEffect(() => {
    setSlotsFrozenAtBeat({ ...slotValuesRef.current });
    setSegmentIndex(0);
    setBlankBuffer('');
    setShowReveal(false);
    setShowHint(false);
    listenOnlySpokenRef.current = false;
    setPhase(
      currentBeat && isListenOnlyBeat(currentBeat) ? 'listenOnly' : 'speaking',
    );
  }, [beatIndex, currentBeat]);

  useEffect(() => {
    if (!script) return;
    syncMorphologyForSlots(slotValues, script, (patch) => {
      setSlotMorph((prev) => {
        const next = { ...prev, ...patch };
        const id = Object.keys(patch)[0];
        if (id && prev[id]?.definite === next[id]?.definite) return prev;
        return next;
      });
    });
  }, [script, slotValues]);

  const segments = useMemo(
    () =>
      script && currentBeat
        ? parseBeatSegments(
            currentBeat.template,
            slotsFrozenAtBeat,
            script,
            slotMorph,
          )
        : [],
    [script, currentBeat, slotsFrozenAtBeat, slotMorph],
  );

  const currentSegment = segments[segmentIndex];

  const blankHint =
    script && currentSegment?.kind === 'blank'
      ? getBlankHint(script, currentSegment.id)
      : undefined;

  useEffect(() => {
    if (showNoVoiceBanner && hasStarted && currentSegment?.kind === 'literal') {
      setShowReveal(true);
    }
  }, [showNoVoiceBanner, hasStarted, currentSegment?.kind]);

  useEffect(() => {
    setShowReveal(false);
    setShowHint(false);
  }, [segmentIndex, beatIndex]);

  const buildRoundResult = useCallback(
    (): RoundResult =>
      statsToResult(statsRef.current, {
        roundId: round.template.id,
        roundTitle: round.template.title,
        mode,
        targetLength: targetLenRef.current,
        completed: true,
      }),
    [round.template.id, round.template.title, mode],
  );

  const finishStory = useCallback(() => {
    setStoryComplete(true);
  }, []);

  const exitToDashboard = useCallback(() => {
    onComplete(buildRoundResult(), 'dashboard');
  }, [onComplete, buildRoundResult]);

  const playAnotherStory = useCallback(() => {
    onComplete(buildRoundResult(), 'another');
  }, [onComplete, buildRoundResult]);

  const finishBeat = useCallback(
    (newSlots: Record<string, string>) => {
      if (!script || !currentBeat) return;
      if (!beatSlotsFilled(currentBeat.template, newSlots)) return;

      const resolved = resolveStoryTemplate(
        currentBeat.template,
        newSlots,
        script,
        slotMorph,
      );
      setCompletedLines((lines) => [...lines, resolved]);
      setBlankBuffer('');

      const nextBeat = beatIndex + 1;
      if (nextBeat >= script.beats.length) {
        finishStory();
        return;
      }

      setBeatIndex(nextBeat);
    },
    [script, currentBeat, beatIndex, finishStory, slotMorph],
  );

  const speakSegmentText = useCallback(
    async (text: string, withMagicCue: boolean) => {
      const speech = text.trim();
      if (!speech) return false;
      cancel();
      setNarrating(true);
      if (audioAvailable) {
        await speak(speech);
        if (withMagicCue && audioEnabled) playStoryMagicCue();
      }
      setNarrating(false);
      return audioAvailable;
    },
    [audioAvailable, audioEnabled, speak, cancel],
  );

  const runListenOnlySpeak = useCallback(async () => {
    if (!script || !currentBeat || speakRunningRef.current) return;
    if (listenOnlySpokenRef.current) return;
    listenOnlySpokenRef.current = true;

    if (!beatSlotsFilled(currentBeat.template, slotValues)) {
      finishBeat(slotValues);
      return;
    }

    const line = beatSpeakText(
      currentBeat.template,
      slotValues,
      script,
      slotMorph,
    );
    if (!line) {
      finishBeat(slotValues);
      return;
    }

    speakRunningRef.current = true;
    await speakSegmentText(line, false);
    speakRunningRef.current = false;
  }, [
    script,
    currentBeat,
    slotValues,
    slotMorph,
    speakSegmentText,
    finishBeat,
  ]);

  const runSegmentSpeak = useCallback(async () => {
    if (!script || !currentSegment || speakRunningRef.current) return;
    if (currentSegment.kind !== 'literal') {
      setPhase('typingBlank');
      return;
    }

    const speech =
      currentSegment.kind === 'literal'
        ? segmentLiteralSpeech(
            currentSegment.text,
            segmentIndex,
            segments,
            slotValues,
            script,
            slotMorph,
          )
        : '';
    if (!speech) {
      setPhase('typingLiteral');
      return;
    }

    speakRunningRef.current = true;
    await speakSegmentText(speech, true);
    speakRunningRef.current = false;
    setPhase('typingLiteral');
  }, [
    script,
    currentSegment,
    segmentIndex,
    segments,
    slotValues,
    slotMorph,
    speakSegmentText,
  ]);

  useEffect(() => {
    if (!hasStarted || !script || !currentBeat || narrating) return;

    if (isListenOnly) {
      if (phase === 'listenOnly') void runListenOnlySpeak();
      return;
    }

    if (phase !== 'speaking') return;
    void runSegmentSpeak();
  }, [
    hasStarted,
    script,
    currentBeat,
    isListenOnly,
    phase,
    segmentIndex,
    beatIndex,
    narrating,
    runSegmentSpeak,
    runListenOnlySpeak,
  ]);

  const advanceAfterBlank = useCallback(
    (word: string) => {
      if (!currentSegment || currentSegment.kind !== 'blank') return;

      const newSlots = { ...slotValues, [currentSegment.id]: word };
      setSlotValues(newSlots);
      setBlankBuffer('');
      targetLenRef.current += word.length;

      const partial: RoundResult = {
        roundId: `${round.template.id}-b${beatIndex}-blank`,
        title: `${round.template.title} · hul`,
        mode,
        targetLength: word.length,
        durationMs: 0,
        wpm: 0,
        accuracy: 1,
        correctChars: word.length,
        totalKeystrokes: word.length,
        mistakes: 0,
        backspaces: 0,
        errorsByKey: {},
        responseTimes: {},
        completed: true,
        hintsUsed: false,
        presentationKind: 'story',
      };
      mergeStats(statsRef.current, partial);

      if (audioEnabled) playSuccessChime();

      const nextSeg = segmentIndex + 1;
      if (nextSeg < segments.length) {
        setSegmentIndex(nextSeg);
        setPhase('speaking');
        return;
      }
      finishBeat(newSlots);
    },
    [
      currentSegment,
      slotValues,
      segmentIndex,
      segments.length,
      finishBeat,
      beatIndex,
      round.template.id,
      round.template.title,
      mode,
      audioEnabled,
    ],
  );

  const handleLiteralFinish = useCallback(
    (partial: RoundResult) => {
      mergeStats(statsRef.current, partial);
      targetLenRef.current += partial.correctChars;
      const nextSeg = segments[segmentIndex + 1];
      if (nextSeg?.kind === 'blank') {
        setSegmentIndex((i) => i + 1);
        setPhase('typingBlank');
      } else {
        finishBeat(slotValues);
      }
    },
    [segments, segmentIndex, slotValues, finishBeat],
  );

  const repeatSpeechText = useMemo(() => {
    if (!script) return '';
    if (isListenOnly && currentBeat) {
      return beatSpeakText(
        currentBeat.template,
        slotValues,
        script,
        slotMorph,
      );
    }
    if (!currentSegment) return '';
    if (currentSegment.kind === 'literal') {
      return segmentLiteralSpeech(
        currentSegment.text,
        segmentIndex,
        segments,
        slotValues,
        script,
        slotMorph,
      );
    }
    return lastLiteralSpeechBefore(
      segments,
      segmentIndex,
      slotValues,
      script,
      slotMorph,
    );
  }, [
    script,
    isListenOnly,
    currentBeat,
    slotValues,
    slotMorph,
    currentSegment,
    segments,
    segmentIndex,
  ]);

  const handleRepeat = useCallback(() => {
    if (!repeatSpeechText) return;
    const withCue =
      !isListenOnly && currentSegment?.kind === 'literal';
    void speakSegmentText(repeatSpeechText, withCue);
  }, [repeatSpeechText, speakSegmentText, isListenOnly, currentSegment?.kind]);

  const handleListenOnlyContinue = useCallback(() => {
    finishBeat(slotValues);
  }, [finishBeat, slotValues]);

  const handleStart = useCallback(() => {
    setHasStarted(true);
    if (showNoVoiceBanner) setShowReveal(true);
  }, [showNoVoiceBanner]);

  const handleToggleReveal = useCallback(() => {
    if (isListenOnly) {
      setShowReveal((v) => !v);
      return;
    }
    if (currentSegment?.kind === 'blank') {
      setShowHint((v) => !v);
    } else {
      setShowReveal((v) => !v);
    }
  }, [isListenOnly, currentSegment?.kind]);

  const listenOnlyLine = useMemo(() => {
    if (!currentBeat || !isListenOnly || !script) return '';
    return beatSpeakText(
      currentBeat.template,
      slotValues,
      script,
      slotMorph,
    );
  }, [currentBeat, isListenOnly, slotValues, script, slotMorph]);

  if (!script || !storyId) {
    return (
      <p className="text-center text-sm text-rose-600 dark:text-rose-300">
        Fortællingen kunne ikke indlæses.
      </p>
    );
  }

  if (!currentBeat) {
    return null;
  }

  if (!hasStarted) {
    return (
      <div className="flex flex-col gap-4">
        <div className="flex items-center justify-between gap-2">
          <p className="label text-faint">Fortælling · {script.title}</p>
          <p className="text-xs text-faint">
            Linje {beatIndex + 1} / {script.beats.length}
          </p>
        </div>
        <StoryStartCard
          voicesReady={voicesReady}
          showNoVoiceBanner={showNoVoiceBanner}
          onStart={handleStart}
        />
      </div>
    );
  }

  if (storyComplete) {
    return (
      <StoryCompleteScreen
        title={script.title}
        lines={completedLines}
        onPlayAnother={playAnotherStory}
        onDashboard={exitToDashboard}
      />
    );
  }

  const showKeyboard =
    !narrating &&
    (phase === 'typingLiteral' || phase === 'typingBlank');

  const visOrdLabel = isListenOnly
    ? showReveal
      ? 'Skjul linje'
      : 'Vis ord'
    : currentSegment?.kind === 'blank'
      ? showHint
        ? 'Skjul hint'
        : 'Vis ord'
      : showReveal
        ? 'Skjul ord'
        : 'Vis ord';

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between gap-2">
        <p className="label text-faint">Fortælling · {script.title}</p>
        <p className="text-xs text-faint">
          Linje {beatIndex + 1} / {script.beats.length}
          {isListenOnly
            ? ' · Lyt'
            : segments.length > 1
              ? ` · Del ${segmentIndex + 1}/${segments.length}`
              : ''}
        </p>
      </div>

      {completedLines.length > 0 && (
        <div className="surface max-h-[min(30vh,12rem)] overflow-y-auto p-4 sm:p-5">
          <p className="label mb-2 text-faint">Din historie</p>
          <div className="space-y-1 font-mono text-base leading-relaxed">
            {completedLines.map((line, i) => (
              <p
                key={i}
                className="whitespace-pre-wrap break-words text-emerald-700 dark:text-emerald-300"
              >
                {line}
              </p>
            ))}
          </div>
        </div>
      )}

      <div className="surface flex flex-col gap-4 p-5 sm:p-6">
        {showNoVoiceBanner && (
          <div className="rounded-xl bg-amber-100 px-4 py-2 text-center text-xs text-amber-900 ring-1 ring-amber-200 dark:bg-amber-500/10 dark:text-amber-200 dark:ring-amber-500/30">
            Ingen dansk stemme fundet. Brug «Vis ord» eller skriv efter
            prikkerne.
          </div>
        )}

        {narrating ? (
          <div className="flex items-center justify-center gap-3 py-8">
            <SpeakerIcon className="h-10 w-10 animate-pulse text-indigo-500" />
            <p className="text-sm font-medium text-strong">Lyt…</p>
          </div>
        ) : isListenOnly ? (
          <StoryListenOnlyPanel
            line={listenOnlyLine}
            showReveal={showReveal}
            narrating={narrating}
            onContinue={handleListenOnlyContinue}
          />
        ) : phase === 'typingLiteral' &&
          currentSegment?.kind === 'literal' ? (
          <StoryLiteralInputBlock
            key={`${beatIndex}-${segmentIndex}`}
            segment={currentSegment}
            segmentIndex={segmentIndex}
            segmentCount={segments.length}
            showReveal={showReveal}
            mode={mode}
            roundId={`${round.template.id}-b${beatIndex}-s${segmentIndex}`}
            roundTitle={round.template.title}
            keyboardLayout={keyboardLayout}
            trin={trin}
            strictCase={strictCase}
            allowAltLayer={allowAltLayer}
            onFinish={handleLiteralFinish}
          />
        ) : currentSegment ? (
          <ActiveSegmentDisplay
            segment={currentSegment}
            phase={phase}
            blankBuffer={blankBuffer}
            showReveal={showReveal}
            showHint={showHint}
            blankHint={blankHint}
          />
        ) : null}

        <StoryListenBar
          narrating={narrating}
          onRepeat={handleRepeat}
          onToggleReveal={handleToggleReveal}
          visOrdLabel={visOrdLabel}
          repeatDisabled={!repeatSpeechText}
        />
      </div>

      {phase === 'typingBlank' && currentSegment?.kind === 'blank' && (
        <StoryBlankTyper
          maxLength={currentSegment.maxLength}
          setBuffer={setBlankBuffer}
          onDone={advanceAfterBlank}
        />
      )}

      {showKeyboard && phase === 'typingBlank' && (
        <div className="space-y-2">
          <Keyboard
            layoutId={keyboardLayout}
            unlockedKeys={trin.unlockedKeys}
            hintLevel="none"
          />
        </div>
      )}

      <p className="text-center text-xs text-faint">
        {isListenOnly
          ? 'Lyt til linjen med dine ord — tryk Fortsæt når du er klar.'
          : 'Lyt — skriv prikkerne bogstav for bogstav og dit ord i understregningerne. Mellemrum går videre.'}
      </p>
    </div>
  );
}

interface StoryCompleteScreenProps {
  title: string;
  lines: string[];
  onDashboard: () => void;
  onPlayAnother: () => void;
}

function StoryCompleteScreen({
  title,
  lines,
  onDashboard,
  onPlayAnother,
}: StoryCompleteScreenProps) {
  useEnterAction(onDashboard);

  return (
    <div className="mx-auto flex w-full max-w-2xl flex-col items-center gap-8 py-6 animate-fade-in-up">
      <header className="text-center">
        <p className="label text-faint">Din historie</p>
        <h2 className="mt-1 text-2xl font-semibold text-strong">{title}</h2>
      </header>

      <div className="surface w-full px-6 py-8 sm:px-10 sm:py-10">
        <div className="space-y-3 text-center font-mono text-xl leading-relaxed tracking-wide sm:text-2xl">
          {lines.map((line, i) => (
            <p
              key={i}
              className="whitespace-pre-wrap break-words text-emerald-700 dark:text-emerald-300"
            >
              {line}
            </p>
          ))}
        </div>
      </div>

      <div className="w-full max-w-md space-y-3 text-center">
        <p className="text-sm text-muted">
          Vil du skrive en fortælling til, eller vende tilbage til oversigten?
        </p>
        <button
          type="button"
          onClick={onPlayAnother}
          className="w-full rounded-xl bg-indigo-500 px-6 py-3 text-base font-semibold text-white shadow-sm transition hover:bg-indigo-400"
        >
          En fortælling til
        </button>
        <button
          type="button"
          autoFocus
          onClick={onDashboard}
          className="w-full rounded-xl bg-zinc-200 px-6 py-3 text-base font-semibold text-zinc-900 transition hover:bg-zinc-300 dark:bg-zinc-700 dark:text-zinc-100 dark:hover:bg-zinc-600"
        >
          Tilbage til dashboard
        </button>
      </div>
    </div>
  );
}

interface StoryListenOnlyPanelProps {
  line: string;
  showReveal: boolean;
  narrating: boolean;
  onContinue: () => void;
}

function StoryListenOnlyPanel({
  line,
  showReveal,
  narrating,
  onContinue,
}: StoryListenOnlyPanelProps) {
  return (
    <div className="flex flex-col items-center gap-4 py-4">
      <p className="label text-indigo-600 dark:text-indigo-300">Lyt til historien</p>
      {showReveal ? (
        <p className="max-w-full text-center font-mono text-2xl leading-relaxed text-emerald-700 dark:text-emerald-300 sm:text-3xl">
          {line}
        </p>
      ) : (
        <p className="text-sm text-muted">···</p>
      )}
      <button
        type="button"
        onClick={onContinue}
        disabled={narrating}
        className="rounded-xl bg-indigo-500 px-6 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-indigo-400 disabled:opacity-50"
      >
        Fortsæt
      </button>
    </div>
  );
}

interface StoryStartCardProps {
  voicesReady: boolean;
  showNoVoiceBanner: boolean;
  onStart: () => void;
}

function StoryStartCard({
  voicesReady,
  showNoVoiceBanner,
  onStart,
}: StoryStartCardProps) {
  return (
    <div className="surface mx-auto w-full max-w-xl p-8 text-center animate-fade-in-up">
      <SpeakerIcon className="mx-auto h-12 w-12 text-indigo-500" />
      <p className="mt-4 text-lg font-semibold text-strong">
        Klar til fortællingen?
      </p>
      <p className="mt-2 text-sm text-muted">
        {!voicesReady
          ? 'Indlæser stemmer…'
          : showNoVoiceBanner
            ? 'Ingen dansk stemme på din enhed — vi viser teksten når du trykker Vis ord.'
            : 'Du hører en sætningsdel, skriver prikkerne og dit eget ord i understregningerne.'}
      </p>
      <button
        type="button"
        autoFocus
        onClick={onStart}
        disabled={!voicesReady}
        className="mt-6 rounded-xl bg-indigo-500 px-6 py-3 text-base font-semibold text-white shadow-sm transition hover:bg-indigo-400 disabled:cursor-not-allowed disabled:opacity-50 dark:shadow-lg dark:shadow-indigo-500/30"
      >
        Start oplæsning
      </button>
      <p className="mt-2 text-xs text-faint">
        Tryk Gentag del eller Vis ord undervejs.
      </p>
    </div>
  );
}

interface StoryListenBarProps {
  narrating: boolean;
  onRepeat: () => void;
  onToggleReveal: () => void;
  visOrdLabel: string;
  repeatDisabled?: boolean;
}

function StoryListenBar({
  narrating,
  onRepeat,
  onToggleReveal,
  visOrdLabel,
  repeatDisabled = false,
}: StoryListenBarProps) {
  return (
    <div className="flex flex-col gap-2 sm:flex-row">
      <button
        type="button"
        onClick={onRepeat}
        disabled={narrating || repeatDisabled}
        className="flex-1 btn-secondary py-2.5 text-sm font-semibold disabled:opacity-50"
      >
        Gentag del
      </button>
      <button
        type="button"
        onClick={onToggleReveal}
        disabled={narrating}
        className="flex-1 btn-secondary py-2.5 text-sm font-semibold disabled:opacity-50"
      >
        {visOrdLabel}
      </button>
    </div>
  );
}

interface ActiveSegmentDisplayProps {
  segment: StorySegment;
  phase: SegmentPhase;
  blankBuffer: string;
  showReveal: boolean;
  showHint: boolean;
  blankHint?: string;
}

function ActiveSegmentDisplay({
  segment,
  phase,
  blankBuffer,
  showReveal,
  showHint,
  blankHint,
}: ActiveSegmentDisplayProps) {
  return (
    <div className="flex min-h-[7rem] flex-col items-center justify-center gap-3">
      <p className="max-w-full whitespace-pre-wrap break-words text-center font-mono text-2xl leading-relaxed tracking-wide sm:text-3xl">
        {segment.kind === 'literal' ? (
          <span className="text-zinc-300 dark:text-zinc-600">
            {renderLiteralDots(segment.text, showReveal)}
          </span>
        ) : (
          <BlankSegmentDisplay
            maxLength={segment.maxLength}
            buffer={blankBuffer}
            active={phase === 'typingBlank'}
            showHint={showHint}
            hint={blankHint}
          />
        )}
      </p>
      {segment.kind === 'blank' && showHint && blankHint ? (
        <p className="max-w-md text-center text-sm text-muted">{blankHint}</p>
      ) : (
        <p className="text-xs text-faint">
          · = bogstav du skriver · _ = dit eget ord
        </p>
      )}
    </div>
  );
}

interface BlankSegmentDisplayProps {
  maxLength: number;
  buffer: string;
  active: boolean;
  showHint: boolean;
  hint?: string;
}

function BlankSegmentDisplay({
  maxLength,
  buffer,
  active,
}: BlankSegmentDisplayProps) {
  const width = blankDisplayWidth(maxLength);
  const chars: ReactNode[] = [];
  for (let i = 0; i < width; i++) {
    const ch = active ? buffer[i] : undefined;
    if (ch) {
      chars.push(
        <span
          key={i}
          className="border-b-2 border-indigo-400 text-emerald-600 dark:text-emerald-300"
        >
          {ch}
        </span>,
      );
    } else {
      chars.push(
        <span
          key={i}
          className="border-b-2 border-zinc-300 text-zinc-400 dark:border-zinc-600 dark:text-zinc-500"
        >
          _
        </span>,
      );
    }
  }
  return <span>{chars}</span>;
}

interface StoryLiteralInputBlockProps {
  segment: Extract<StorySegment, { kind: 'literal' }>;
  segmentIndex: number;
  segmentCount: number;
  showReveal: boolean;
  mode: PracticeMode;
  roundId: string;
  roundTitle: string;
  keyboardLayout: KeyboardLayoutId;
  trin: Trin;
  strictCase?: boolean;
  allowAltLayer?: boolean;
  onFinish: (r: RoundResult) => void;
}

function StoryLiteralInputBlock({
  segment,
  showReveal,
  mode,
  roundId,
  roundTitle,
  keyboardLayout,
  trin,
  strictCase,
  allowAltLayer,
  onFinish,
}: StoryLiteralInputBlockProps) {
  const input = useRoundInput({
    targetText: segment.text,
    mode,
    roundId,
    roundTitle,
    presentationKind: 'story',
    onFinish,
    strictCase,
    allowAltLayer,
    keyboardLayoutId: keyboardLayout,
  });

  return (
    <>
      <div className="flex min-h-[7rem] flex-col items-center justify-center gap-3">
        <p className="max-w-full whitespace-pre-wrap break-words text-center font-mono text-2xl leading-relaxed tracking-wide sm:text-3xl">
          {renderLiteralTyped(
            {
              targetText: segment.text,
              cursor: input.cursor,
              correctMask: input.correctMask,
            },
            showReveal,
          )}
        </p>
        <p className="text-xs text-faint">
          · = bogstav du skriver · _ = dit eget ord
        </p>
      </div>
      <div className="space-y-2">
        <Keyboard
          layoutId={keyboardLayout}
          unlockedKeys={trin.unlockedKeys}
          lastPressedKey={input.lastPress?.key}
          lastPressCorrect={input.lastPress?.correct}
          hintLevel={input.hintLevel}
        />
      </div>
    </>
  );
}

interface LiteralInputVisual {
  targetText: string;
  cursor: number;
  correctMask: ReadonlyArray<boolean>;
}

function renderLiteralTyped(
  input: LiteralInputVisual,
  revealText: boolean,
): ReactNode {
  const { targetText, cursor, correctMask } = input;
  const letters: ReactNode[] = [];
  for (let i = 0; i < targetText.length; i++) {
    const typed = correctMask[i];
    const isCursor = i === cursor;
    const ch = targetText[i]!;

    if (typed) {
      letters.push(
        <span key={i} className="text-emerald-600 dark:text-emerald-300">
          {ch === ' ' ? '\u00A0' : ch}
        </span>,
      );
    } else if (isCursor) {
      letters.push(
        <span
          key={i}
          className="inline-block min-w-[0.6ch] rounded-sm border-l-2 border-indigo-500 bg-indigo-100 text-zinc-900 dark:bg-indigo-500/10 dark:text-zinc-100"
        >
          {revealText && ch !== ' ' ? ch : '\u00A0'}
        </span>,
      );
    } else if (revealText) {
      letters.push(
        <span key={i} className="text-zinc-400 dark:text-zinc-500">
          {ch === ' ' ? '\u00A0' : ch}
        </span>,
      );
    } else if (ch === ' ') {
      letters.push(
        <span key={i} className="text-zinc-300 dark:text-zinc-600">
          {'\u00A0'}
        </span>,
      );
    } else {
      letters.push(
        <span key={i} className="text-zinc-300 dark:text-zinc-600">
          ·
        </span>,
      );
    }
  }
  return <>{letters}</>;
}

function renderLiteralDots(text: string, revealText: boolean): ReactNode {
  if (revealText) {
    return (
      <span className="text-zinc-400 dark:text-zinc-500">{text}</span>
    );
  }
  const parts: ReactNode[] = [];
  for (let i = 0; i < text.length; i++) {
    const ch = text[i]!;
    if (ch === ' ') {
      parts.push(
        <span key={i} className="text-zinc-300 dark:text-zinc-600">
          {'\u00A0'}
        </span>,
      );
    } else {
      parts.push(
        <span key={i} className="text-zinc-300 dark:text-zinc-600">
          ·
        </span>,
      );
    }
  }
  return <>{parts}</>;
}

interface StoryBlankTyperProps {
  maxLength: number;
  setBuffer: Dispatch<SetStateAction<string>>;
  onDone: (word: string) => void;
}

function StoryBlankTyper({
  maxLength,
  setBuffer,
  onDone,
}: StoryBlankTyperProps) {
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (shouldIgnoreKeyEvent(e)) return;

      if (e.key === 'Backspace') {
        e.preventDefault();
        setBuffer((b) => b.slice(0, -1));
        return;
      }

      if (e.key === ' ' || e.key === 'Enter') {
        e.preventDefault();
        setBuffer((b) => {
          const w = b.trim();
          if (w.length > 0) onDone(w);
          return '';
        });
        return;
      }

      if (!isPrintableTypingKey(e.key) || e.key === ' ') return;

      setBuffer((b) => {
        if (b.length >= maxLength) return b;
        return b + e.key;
      });
      e.preventDefault();
    };

    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [maxLength, setBuffer, onDone]);

  return (
    <div className="flex flex-col items-center gap-2">
      <p className="text-sm font-medium text-strong">
        Skriv dit ord i understregningerne
      </p>
      <SpaceAdvanceHint />
    </div>
  );
}

function SpeakerIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.5}
      aria-hidden
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M11.25 4.5l-6 4.5v6l6 4.5V4.5z"
      />
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M15.75 9.75a3 3 0 010 4.5m3-6a6 6 0 010 9"
      />
    </svg>
  );
}
